<template>
  <div class="grid gap-5 pt-2">
    <p
      v-if="sortedYears.length === 0"
      class="kb-row-meta m-0"
    >
      No purchases yet
    </p>

    <section
      v-for="year in sortedYears"
      :key="year"
      class="grid gap-1"
    >
      <h3 class="kb-label m-0">
        {{ Number(year) - 1 }}–{{ year }} season
      </h3>

      <!-- One line per purchase: type, what it was, when and where, amount -->
      <ul
        v-if="getYearPurchaseCount(year) > 0"
        class="kb-purchases"
      >
        <li
          v-for="(item, index) in getAllPurchasesForYear(year)"
          :key="`${year}-${index}`"
          class="kb-purchase"
        >
          <!-- Color: the product; words: how the member got it -->
          <span
            class="kb-tag"
            :class="productTagClass(item.type)"
          >{{ purchaseTagText(item.type, acquisitionOf(item)) }}</span>

          <div class="min-w-0">
            <p class="kb-purchase-what">
              <template v-if="item.type === 'bulk'">
                {{ item.quantity }} memberships<span
                  v-if="item.subkreweId"
                  class="text-kb-muted"
                > · {{ getSubkreweName(item.subkreweId) }}</span><span
                  v-if="item.discountPercent"
                  class="text-kb-accent"
                > · {{ item.discountPercent }}% off {{ item.discountedQuantity }}</span>
              </template>
              <template v-else-if="item.pooled">
                Pool ticket<span
                  v-if="item.subkreweId"
                  class="text-kb-muted"
                > · {{ getSubkreweName(item.subkreweId) }}</span>
              </template>
              <template v-else-if="isEventbritePurchase(item)">
                <span
                  v-if="hasOwnTicketType(item)"
                  class="capitalize"
                >{{ item.ticketType }}</span>
                <span
                  v-else-if="hasAccessCode(item)"
                  class="font-mono"
                >{{ item.accessCode }}</span>
                <template v-else>
                  {{ getTypeLabel(item.type) }} ticket
                </template>
              </template>
              <template v-else>
                {{ item.productName || getTypeLabel(item.type) }}<span
                  v-if="item.tierLabel"
                  class="text-kb-muted"
                > · {{ item.tierLabel }}</span><span
                  v-if="item.quantity && item.quantity > 1"
                  class="text-kb-muted"
                > ×{{ item.quantity }}</span>
              </template>
            </p>

            <p class="kb-row-meta m-0 flex flex-wrap items-center gap-x-1.5">
              <span>{{ formatPurchaseDate(getPurchaseDisplayDate(item)) }}</span>
              <!-- Pool ticket -->
              <template v-if="item.pooled">
                <span v-if="item.redeemCode">· code <span class="font-mono">{{ item.redeemCode }}</span></span>
                <span v-if="item.redeemedByAdmin">· redeemed by {{ getAdminName(item.redeemedByAdmin) }}</span>
              </template>
              <!-- Comped by an admin -->
              <span v-else-if="item.issuedBy">· comped by {{ getAdminName(item.issuedBy) }}</span>
              <!-- Stripe -->
              <template v-else-if="item.stripeSessionId">
                <span>·</span>
                <a
                  :href="getStripeUrl(item)"
                  target="_blank"
                  class="hover:text-kb-accent hover:underline"
                  @click.stop
                >Stripe <i class="pi pi-arrow-up-right !text-[9px]" /></a>
                <button
                  v-if="item.stripePaymentIntentId || item.stripeSessionId"
                  v-tooltip.top="'Copy transaction ID'"
                  type="button"
                  class="kb-purchase-copy"
                  aria-label="Copy transaction ID"
                  @click.stop="copyToClipboard(item.stripePaymentIntentId || item.stripeSessionId)"
                >
                  <i class="pi pi-copy" />
                </button>
              </template>
              <!-- Legacy admin-prefixed orders (comped); check before Eventbrite -->
              <span v-else-if="isLegacyAdminComp(item)">· admin override</span>
              <!-- Eventbrite -->
              <template v-else-if="isEventbritePurchase(item)">
                <span>·</span>
                <a
                  :href="getEventbriteUrl(item)"
                  target="_blank"
                  class="hover:text-kb-accent hover:underline"
                  @click.stop
                >Eventbrite <i class="pi pi-arrow-up-right !text-[9px]" /></a>
                <span
                  v-if="getEventbriteOrderId(item)"
                  :title="getEventbriteOrderId(item) ?? undefined"
                >#{{ truncateOrderId(getEventbriteOrderId(item)!) }}</span>
                <span
                  v-if="hasOwnTicketType(item) && hasAccessCode(item)"
                  class="max-w-[10rem] truncate"
                  :title="item.accessCode"
                >· {{ item.accessCode }}</span>
              </template>
            </p>
          </div>

          <span
            v-if="item.unitAmount"
            class="kb-purchase-amount"
          >${{ (item.unitAmount / 100).toFixed(2) }}</span>
        </li>
      </ul>
      <p
        v-else
        class="kb-row-meta m-0"
      >
        No purchases recorded
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Member } from '~/shared/types/member.js'
import { formatDate } from '~/utils/dateUtils'
import { acquisitionOf, productTagClass, purchaseTagText } from '~/utils/purchaseDisplay'

// Purchase item type that covers both legacy Eventbrite and new Stripe purchases
type FirestoreDate = Date | { toDate?: () => Date } | { _seconds: number, _nanoseconds: number }

interface PurchaseItem {
  orderId?: string
  purchasedAt?: FirestoreDate
  ticketType?: string
  accessCode?: string
  status?: string
  eventbriteOrderId?: string
  stripeSessionId?: string
  stripePaymentIntentId?: string
  issuedBy?: string
  quantity?: number
  childrenIds?: string[]
  productName?: string
  tierLabel?: string
  unitAmount?: number
  // Pool ticket fields
  pooled?: boolean
  redeemCode?: string
  subkreweId?: string
  poolTicketId?: string
  redeemedByAdmin?: string
  redeemedAt?: FirestoreDate
  paidAt?: FirestoreDate
  // Bulk purchase fields
  discountPercent?: number
  discountedQuantity?: number
}

interface Props {
  member: Member
}

const props = defineProps<Props>()

// Access stores for name lookups
const membersStore = useMembersStore()
const subkrewesStore = useSubKrewesStore()

// Get admin name by uid
function getAdminName(uid?: string): string {
  if (!uid) return 'Unknown'
  const member = membersStore.members[`members/${uid}`]
  return member?.name || uid.substring(0, 8) + '...'
}

// Get subkrewe name by id
function getSubkreweName(subkreweId?: string): string {
  if (!subkreweId) return 'Unknown'
  const subkrewe = subkrewesStore.subkrewes[`subkrewes/${subkreweId}`]
  return subkrewe?.name || subkreweId.substring(0, 8) + '...'
}

// Get all years with purchases, sorted descending
const sortedYears = computed(() => {
  const purchases = props.member.purchases
  if (!purchases) return []

  return Object.keys(purchases)
    .filter((year) => {
      const yearData = purchases[year]
      // Only include years that have actual purchase data
      return yearData && (
        (Array.isArray(yearData.membership) && yearData.membership.length > 0)
        || (Array.isArray(yearData.vip) && yearData.vip.length > 0)
        || (Array.isArray(yearData.parking) && yearData.parking.length > 0)
        || (Array.isArray(yearData.children) && yearData.children.length > 0)
        || (Array.isArray(yearData.bulk) && yearData.bulk.length > 0)
      )
    })
    .sort((a, b) => parseInt(b) - parseInt(a))
})

// Get purchases by type for a specific year
function getPurchasesByType(year: string, type: 'membership' | 'vip' | 'parking' | 'children' | 'bulk'): PurchaseItem[] {
  const yearData = props.member.purchases?.[year]
  if (!yearData) return []

  const purchases = yearData[type]
  if (!Array.isArray(purchases)) return []

  return purchases as PurchaseItem[]
}

// Get all purchases for a year as a flat array with type info
function getAllPurchasesForYear(year: string): (PurchaseItem & { type: string })[] {
  const result: (PurchaseItem & { type: string })[] = []

  for (const type of ['membership', 'vip', 'parking', 'children', 'bulk'] as const) {
    const purchases = getPurchasesByType(year, type)
    for (const p of purchases) {
      result.push({ ...p, type })
    }
  }

  // Sort by date descending
  return result.sort((a, b) => {
    const dateA = getPurchaseTimestamp(getPurchaseDisplayDate(a))
    const dateB = getPurchaseTimestamp(getPurchaseDisplayDate(b))
    return dateB - dateA
  })
}

function getPurchaseTimestamp(date?: FirestoreDate): number {
  if (!date) return 0
  // Handle Firestore Timestamp with _seconds
  if (typeof (date as { _seconds?: number })._seconds === 'number') {
    return (date as { _seconds: number })._seconds * 1000
  }
  // Handle Firestore Timestamp with toDate method
  if (typeof (date as { toDate?: () => Date }).toDate === 'function') {
    return (date as { toDate: () => Date }).toDate().getTime()
  }
  // Handle native Date
  return (date as Date).getTime()
}

// Get label for purchase type
// Eventbrite tickets carry a ticket type that usually just repeats the
// purchase type; only show it when it says something the tag doesn't
function hasOwnTicketType(item: PurchaseItem & { type: string }): boolean {
  return Boolean(item.ticketType) && item.ticketType!.toLowerCase() !== getTypeLabel(item.type).toLowerCase()
}

function hasAccessCode(item: PurchaseItem): boolean {
  return Boolean(item.accessCode) && item.accessCode !== 'admin-override'
}

function getTypeLabel(type: string): string {
  switch (type) {
    case 'membership': return 'Membership'
    case 'vip': return 'VIP'
    case 'parking': return 'Parking'
    case 'children': return 'Children'
    case 'bulk': return 'Bulk'
    default: return type
  }
}

// Phosphor tag for a purchase type: a membership is paid dues (OK), VIP is an
// honor (purple), everything else is neutral. Gold and red mean "needs
// attention" and "error", which no purchase type does.

// Get Stripe dashboard URL
function getStripeUrl(purchase: PurchaseItem): string {
  const id = purchase.stripePaymentIntentId || purchase.stripeSessionId
  if (purchase.stripePaymentIntentId) {
    return `https://dashboard.stripe.com/payments/${id}`
  }
  return `https://dashboard.stripe.com/checkout/sessions/${id}`
}

// Get Eventbrite URL
function getEventbriteUrl(purchase: PurchaseItem): string {
  const orderId = purchase.eventbriteOrderId || purchase.orderId
  return `https://www.eventbrite.com/organizations/orders/${orderId}`
}

// Check if purchase is a real Eventbrite purchase (not admin-prefixed)
function isEventbritePurchase(purchase: PurchaseItem): boolean {
  // Has Eventbrite order ID that doesn't start with admin-
  if (purchase.eventbriteOrderId && !purchase.eventbriteOrderId.startsWith('admin-')) {
    return true
  }
  // Has numeric orderId (Eventbrite orders are numeric)
  if (purchase.orderId && !purchase.orderId.startsWith('admin-') && /^\d+$/.test(purchase.orderId)) {
    return true
  }
  return false
}

// Check if purchase is a legacy admin comp (admin- prefixed orders)
function isLegacyAdminComp(purchase: PurchaseItem): boolean {
  return !!(purchase.orderId?.startsWith('admin-') || purchase.eventbriteOrderId?.startsWith('admin-'))
}

// Get Eventbrite order ID from purchase
function getEventbriteOrderId(purchase: PurchaseItem): string | null {
  if (purchase.eventbriteOrderId && !purchase.eventbriteOrderId.startsWith('admin-')) {
    return purchase.eventbriteOrderId
  }
  if (purchase.orderId && !purchase.orderId.startsWith('admin-') && /^\d+$/.test(purchase.orderId)) {
    return purchase.orderId
  }
  return null
}

// Truncate long order IDs for display
function truncateOrderId(orderId: string, maxLen = 8): string {
  if (orderId.length <= maxLen) return orderId
  return orderId.slice(0, maxLen) + '…'
}

// Copy to clipboard
function copyToClipboard(text: string): void {
  navigator.clipboard.writeText(text)
}

// Count total purchases for a year
function getYearPurchaseCount(year: string): number {
  return getPurchasesByType(year, 'membership').length
    + getPurchasesByType(year, 'vip').length
    + getPurchasesByType(year, 'parking').length
    + getPurchasesByType(year, 'children').length
    + getPurchasesByType(year, 'bulk').length
}

// Format purchase date as MM/DD/YYYY
function formatPurchaseDate(date?: FirestoreDate): string {
  if (!date) return ''

  // Handle Firestore Timestamp with _seconds
  if (typeof (date as { _seconds?: number })._seconds === 'number') {
    const jsDate = new Date((date as { _seconds: number })._seconds * 1000)
    return formatDate(jsDate, { format: 'MM/dd/yyyy' })
  }

  // Handle Firestore Timestamp with toDate method
  if (typeof (date as { toDate?: () => Date }).toDate === 'function') {
    return formatDate((date as { toDate: () => Date }).toDate(), { format: 'MM/dd/yyyy' })
  }

  // Handle native Date
  return formatDate(date as Date, { format: 'MM/dd/yyyy' })
}

// Get the best date to display for a purchase
function getPurchaseDisplayDate(purchase: PurchaseItem): FirestoreDate | undefined {
  return purchase.purchasedAt || purchase.redeemedAt || purchase.paidAt
}
</script>

<style scoped>
.kb-purchases {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* The tag column fits the longest "product · how" ("Membership · discounted") */
.kb-purchase {
  display: grid;
  grid-template-columns: 11rem minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 0.125rem 0.75rem;
  padding: 0.5rem 0;
}

/* Narrow (a phone, a narrow drawer): the tag sits on its own line above */
@media (max-width: 639px) {
  .kb-purchase {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .kb-purchase > .kb-tag {
    grid-column: 1 / -1;
    margin-bottom: 0.375rem;
  }
}

.kb-purchase + .kb-purchase {
  border-top: 1px solid var(--kb-line);
}

.kb-purchase .kb-tag {
  justify-self: start;
}

.kb-purchase-what {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--kb-fg);
  overflow-wrap: anywhere;
}

.kb-purchase-amount {
  font: 13px var(--kb-font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--kb-fg);
}

.kb-purchase-copy {
  padding: 0 0.125rem;
  border: 0;
  background: none;
  color: var(--kb-dim);
  cursor: pointer;
}

.kb-purchase-copy:hover {
  color: var(--kb-accent);
}

.kb-purchase-copy .pi {
  font-size: 11px;
}
</style>
